import {
  ENTRY_DATA_SECTION_KEYS as SECTION_KEYS,
  ENTRY_DATA_DRUG_PROFILE_GROUP_TABLE_KEY as DRUG_PROFILE,
} from "@frontend/common";

import useArticleSection from "@/components/business/FillInTableContainer/hooks/useArticleSection";
import { ArticleSection } from "@/types/articleEntryData.types";
import { useArticleInfoQuery } from "@api/articleInfo/useArticleInfo";
import { DRAFT_STATUS_CODE } from "@constants/draftStatusCode.constants";
import { useFillInTableRoles } from "@libs/roles/hooks/useFillInTableRoles";
import { getPressIdFromURL } from "@utils/getStateFromURL";

import { ENABLE_GROUP_TABLE_KEY_LIST } from "../constants/tableCreatorGroupTable.constants";

import type { GroupTableKeyType } from "../types/tableCreator.types";

export const useCanEditGroupTable = (articleSectionKey?: ArticleSection) => {
  const articleSectionByClassification = useArticleSection();
  const articleSection = articleSectionKey || articleSectionByClassification;
  const { data: articleInfo } = useArticleInfoQuery(
    Number(getPressIdFromURL()),
  );

  const { hasEditHalfTableRoles, hasEditFullTableRoles } =
    useFillInTableRoles();

  const checkIsCtgUnsuitableStatus = () => {
    if (articleSection !== SECTION_KEYS.ctg) return false;

    const isUnsuitableStatus =
      articleInfo?.draftArticleStatus === DRAFT_STATUS_CODE.unsuitable;

    if (isUnsuitableStatus) return true;

    return false;
  };

  const checkTableRolesFromTableList = (groupTableKey: GroupTableKeyType) => {
    if (
      articleSection === SECTION_KEYS.clinicalTrial ||
      articleSection === SECTION_KEYS.regulatory ||
      articleSection === SECTION_KEYS.expeditedProgram
    ) {
      if (groupTableKey === DRUG_PROFILE) {
        return hasEditHalfTableRoles;
      }
      return hasEditFullTableRoles;
    }
    if (
      articleSection === SECTION_KEYS.clinicalTrialStatus ||
      articleSection === SECTION_KEYS.companyWebsiteVariousPages ||
      articleSection === SECTION_KEYS.publicationJournal ||
      articleSection === SECTION_KEYS.conferenceSchedule
    ) {
      return hasEditHalfTableRoles;
    }

    if (
      articleSection === SECTION_KEYS.deals ||
      articleSection === SECTION_KEYS.dealsLaSc ||
      articleSection === SECTION_KEYS.ventureCapital ||
      articleSection === SECTION_KEYS.publication ||
      articleSection === SECTION_KEYS.dealFiling ||
      articleSection === SECTION_KEYS.fundingFiling ||
      articleSection === SECTION_KEYS.ctg ||
      articleSection === SECTION_KEYS.regulatoryLabel ||
      articleSection === SECTION_KEYS.patent
    ) {
      return hasEditFullTableRoles;
    }
  };

  const checkDisableEdit = (groupTableKey: GroupTableKeyType) => {
    if (!articleInfo)
      return false || !checkTableRolesFromTableList(groupTableKey);

    const enableGroupTableKeyList = ENABLE_GROUP_TABLE_KEY_LIST[articleSection];

    const isEnableEditByStatus = enableGroupTableKeyList[
      groupTableKey as keyof typeof enableGroupTableKeyList
    ].includes(articleInfo.draftArticleStatus);

    if (checkIsCtgUnsuitableStatus()) return true;

    return (
      !isEnableEditByStatus || !checkTableRolesFromTableList(groupTableKey)
    );
  };

  return {
    checkDisableEdit,
  };
};
